import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Notification.css';

/** Over modaler (2000), PWA-prompt (1800) og admin-drawers */
const NOTIFICATION_Z_INDEX = 10000;

function NotificationIcon({ type }) {
  const mark = {
    success: 'M8.2 12.3 10.8 14.9 15.9 9.4',
    error: 'M9 9l6 6M15 9l-6 6',
    warning: 'M12 8v5',
    info: 'M12 11v5',
  }[type] || 'M12 11v5';

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d={mark} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {type === 'warning' ? <circle cx="12" cy="16.4" r="0.8" fill="currentColor" /> : null}
      {type !== 'success' && type !== 'error' && type !== 'warning' ? (
        <circle cx="12" cy="8.1" r="0.8" fill="currentColor" />
      ) : null}
    </svg>
  );
}

const Notification = ({
  open,
  onClose,
  message,
  label,
  icon,
  autoHideDuration = 4000,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
  type = 'info'
}) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const typeClass = useMemo(() => {
    switch (type) {
      case 'success':
        return 'notification-success';
      case 'error':
        return 'notification-error';
      case 'warning':
        return 'notification-warning';
      default:
        return 'notification-info';
    }
  }, [type]);

  useEffect(() => {
    if (!open || !autoHideDuration) return undefined;
    const timer = setTimeout(() => onCloseRef.current?.(), autoHideDuration);
    return () => clearTimeout(timer);
  }, [open, autoHideDuration, message]);

  if (!open || typeof document === 'undefined') return null;

  const labelText = typeof label === 'string' ? label.trim() : '';
  const vertical = anchorOrigin?.vertical === 'bottom'
    ? 'bottom'
    : anchorOrigin?.vertical === 'center'
      ? 'middle'
      : 'top';
  const horizontal = ['left', 'right'].includes(anchorOrigin?.horizontal) ? anchorOrigin.horizontal : '';

  return createPortal(
    <div
      className={`notification-portal notification-portal--${vertical}${horizontal ? ` notification-portal--${horizontal}` : ''}`}
      style={{ zIndex: NOTIFICATION_Z_INDEX }}
    >
      <div
        key={message}
        className={`notification-snackbar ${typeClass}${labelText ? '' : ' notification-snackbar--plain'}`}
        role="alert"
        aria-live="assertive"
      >
        <span className="notification-accent" aria-hidden="true" />

        <div className="notification-icon-wrap" aria-hidden="true">
          {icon ? (
            <span className="notification-icon-custom">{icon}</span>
          ) : (
            <NotificationIcon type={type} />
          )}
        </div>

        <div className="notification-body">
          {labelText ? <span className="notification-label">{labelText}</span> : null}
          <div className="notification-content">{message}</div>
        </div>

        <button
          type="button"
          className="notification-close"
          onClick={() => onCloseRef.current?.()}
          aria-label="Luk besked"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
          </svg>
        </button>

        {autoHideDuration > 0 && (
          <span className="notification-progress-track" aria-hidden="true">
            <span
              className="notification-progress"
              style={{ animationDuration: `${autoHideDuration}ms` }}
            />
          </span>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Notification;
